// @givmo/app-charity — build/runtime env (Vite `import.meta.env`).
//
// Like the admin SPA (and unlike the SSR donate app which reads RUNTIME `process.env` per request),
// the charity portal is a STATIC SPA: its config is read from Vite's `import.meta.env` at BUILD time
// (the deploy pipeline injects `VITE_*` vars per environment). No secrets are baked — the Firebase
// web config + the reCAPTCHA Enterprise site key + the Sentry DSN are all public in the web-client
// sense, but still per-environment and therefore injected, never hardcoded.
//
// The API base URL is env-injected (NO hardcoded prod URL as the only option): `VITE_API_BASE_URL`
// wins when set, otherwise a per-env default keyed off `VITE_GIVMO_ENV` (api-dev.givmo.io for
// dev/local, api-staging.givmo.io for staging, api.givmo.io for prod). The charity portal uses the
// SAME backend as the other apps; the charity-specific `/charity-admin/*` surface (CPA-BE1) does
// not exist yet, so this scaffold only exercises the shared auth endpoints.

import { donateWebBaseUrl } from '@givmo/fundraisers-module';

/** The deploy environments. Read from `VITE_GIVMO_ENV`, defaulting to `local`. */
export type DeployEnv = 'local' | 'dev' | 'staging' | 'prod';

const DEPLOY_ENVS: readonly DeployEnv[] = ['local', 'dev', 'staging', 'prod'];

/**
 * Per-env API base default. Env-injected `VITE_API_BASE_URL` overrides this. dev/local point at the
 * dev API, staging at staging, and prod at production. The branded hosts are the defaults; an
 * explicit `VITE_API_BASE_URL` (e.g. a Cloud Run URL) takes priority.
 */
const API_BASE_URL_BY_ENV: Record<DeployEnv, string> = {
  local: 'https://api-dev.givmo.io',
  dev: 'https://api-dev.givmo.io',
  staging: 'https://api-staging.givmo.io',
  prod: 'https://api.givmo.io',
};

/** Firebase web config — public (client-shippable) per the Firebase web SDK model. */
export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  measurementId?: string;
}

/** The validated, typed env subset the app consumes. NEVER includes secrets. */
export interface CharityEnv {
  deployEnv: DeployEnv;
  apiBaseUrl: string;
  firebase: FirebaseWebConfig;
  recaptchaEnterpriseSiteKey: string;
  appCheckDebugToken?: string;
  sentryDsn?: string;
  /** App marketing version, stamped into telemetry (X-Client-Version / Sentry release). */
  appVersion: string;
  /**
   * §321 fundraiser-attribution rows section (FCA-WEB1) build-time knob. Default OFF; the section
   * self-gates on this rather than the caller checking it, so a component just imports
   * {@link isFundraiserRowsUiEnabled}.
   */
  fundraiserRowsUiEnabled: boolean;
  /** The shared Fundraisers module's sole charity build reveal knob. Default OFF. */
  fundraisersModuleEnabled: boolean;
  /**
   * The Fundraisers page's "Started by supporters" tab (FCM-WEB3). Default OFF. Reveals the tab only;
   * the backend's retirement flag (`FUNDRAISER_OPT_OUT_RETIREMENT_ENABLED`) and module flags stay
   * authoritative — dark, the list answers the route miss and the tab shows its calm notice.
   */
  supporterFundraisersEnabled: boolean;
  /**
   * The shared organization QR design editor (FCM-WEB4) and, with `profilePageEnabled`, the Profile
   * page's QR card (PE-WEB11). Default OFF; the backend's `ORG_QR_DESIGN_ENABLED` stays authoritative.
   */
  orgQrDesignEnabled: boolean;
  /**
   * The Fundraiser Creation Wizard (EMC-WEB7): New fundraiser opens it instead of the one-page create
   * form. Default OFF; its campaign questions need the backend's `CHARITY_P2P_ENABLED` and
   * `CAMPAIGN_ENDS_ENABLED` on in the same environment.
   */
  fundraiserWizardEnabled: boolean;
  /**
   * The Profile page's Branding and Collateral tabs (EMC-WEB11). Default OFF; shows only with
   * `profilePageEnabled`. The backend's `EMAIL_CAMPAIGNS_ENABLED` (with the console and Fundraisers
   * module flags) stays authoritative: dark, each tab shows its calm not-available state.
   */
  orgBrandingEnabled: boolean;
  /**
   * Campaign groups: the creation wizard asks a campaign "Will people raise in groups?". Default OFF;
   * groups aren't built, so "Yes" only says they are coming. Shows only with the wizard's own knob on.
   */
  campaignGroupsEnabled: boolean;
  /** Books route, sidebar and activation-handler reveal. Default OFF. */
  booksModuleEnabled: boolean;
  /** The tabbed Profile page (PE-WEB1) is revealed — `/profile` swaps to it and its row re-keys. */
  profilePageEnabled: boolean;
  /** Charity-owned donor analytics at `/analytics`. Default OFF; independent from the CRM module. */
  charityDonorAnalyticsEnabled: boolean;
  /**
   * Charity compliance filing register (CCM-WEB1) nav/route reveal knob. Default OFF. This ONLY
   * controls whether the compliance nav items + routes appear in a given env build — the backend
   * `CHARITY_COMPLIANCE_MODULE_ENABLED` flag (layered under `CHARITY_PORTAL_ENABLED`) remains the
   * authoritative runtime gate; all 20 `/charity-admin/compliance` operations 404 while it is off.
   */
  complianceUiEnabled: boolean;
  /**
   * Back-office enrollment surface (CBO-IN3) route reveal knob. Default OFF. This ONLY controls
   * whether the enrollment routes and the dashboard's getting-started step appear in a given env
   * build — the backend `CHARITY_BACKOFFICE_ENABLED` flag (layered under `CHARITY_PORTAL_ENABLED`)
   * remains the authoritative runtime gate; every `/charity-admin/enrollment` operation answers 404
   * while it is off. Flip order is the backend flag first in the same environment, then this knob.
   */
  enrollmentUiEnabled: boolean;
}

function normalizeDeployEnv(raw: string | undefined): DeployEnv {
  const value = (raw ?? '').trim().toLowerCase();
  return (DEPLOY_ENVS as readonly string[]).includes(value) ? (value as DeployEnv) : 'local';
}

function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

function required(value: string | undefined, name: string): string {
  const parsed = optional(value);
  if (parsed === undefined) {
    throw new Error(`Missing required charity environment variable: ${name}.`);
  }
  return parsed;
}

/** Coerce a build-time boolean-ish env string: exactly `'true'` (case/whitespace-insensitive) is ON;
 *  anything else — unset, `'false'`, garbage — is OFF. Never throws. */
function booleanFlag(value: string | undefined): boolean {
  return (value ?? '').trim().toLowerCase() === 'true';
}

/**
 * Read + validate the typed env subset from a Vite env record. Exposed for testing with an injected
 * record; production calls {@link readCharityEnv} which passes `import.meta.env`.
 */
export function parseCharityEnv(source: ImportMetaEnv): CharityEnv {
  const deployEnv = normalizeDeployEnv(source.VITE_GIVMO_ENV);
  const apiBaseUrl = optional(source.VITE_API_BASE_URL) ?? API_BASE_URL_BY_ENV[deployEnv];

  const firebase: FirebaseWebConfig = {
    apiKey: required(source.VITE_FIREBASE_API_KEY, 'VITE_FIREBASE_API_KEY'),
    authDomain: required(source.VITE_FIREBASE_AUTH_DOMAIN, 'VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: required(source.VITE_FIREBASE_PROJECT_ID, 'VITE_FIREBASE_PROJECT_ID'),
    appId: required(source.VITE_FIREBASE_APP_ID, 'VITE_FIREBASE_APP_ID'),
    storageBucket: optional(source.VITE_FIREBASE_STORAGE_BUCKET),
    messagingSenderId: optional(source.VITE_FIREBASE_MESSAGING_SENDER_ID),
    measurementId: optional(source.VITE_FIREBASE_MEASUREMENT_ID),
  };

  return {
    deployEnv,
    apiBaseUrl,
    firebase,
    recaptchaEnterpriseSiteKey: optional(source.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY) ?? '',
    appCheckDebugToken: optional(source.VITE_APP_CHECK_DEBUG_TOKEN),
    sentryDsn: optional(source.VITE_SENTRY_DSN),
    appVersion: optional(source.VITE_APP_VERSION) ?? '0.0.0',
    fundraiserRowsUiEnabled: booleanFlag(source.VITE_FUNDRAISER_ROWS_UI_ENABLED),
    fundraisersModuleEnabled: booleanFlag(source.VITE_FUNDRAISERS_MODULE_ENABLED),
    supporterFundraisersEnabled: booleanFlag(source.VITE_SUPPORTER_FUNDRAISERS_ENABLED),
    orgQrDesignEnabled: booleanFlag(source.VITE_ORG_QR_DESIGN_ENABLED),
    fundraiserWizardEnabled: booleanFlag(source.VITE_FUNDRAISER_WIZARD_ENABLED),
    orgBrandingEnabled: booleanFlag(source.VITE_ORG_BRANDING_ENABLED),
    campaignGroupsEnabled: booleanFlag(source.VITE_CAMPAIGN_GROUPS_ENABLED),
    booksModuleEnabled: booleanFlag(source.VITE_BOOKS_MODULE_ENABLED),
    profilePageEnabled: booleanFlag(source.VITE_PROFILE_PAGE_ENABLED),
    charityDonorAnalyticsEnabled: booleanFlag(source.VITE_CHARITY_DONOR_ANALYTICS_ENABLED),
    complianceUiEnabled: booleanFlag(source.VITE_COMPLIANCE_UI_ENABLED),
    enrollmentUiEnabled: booleanFlag(source.VITE_ENROLLMENT_UI_ENABLED),
  };
}

/** The API's base URL alone, as {@link parseCharityEnv} derives it, without the boot-time requirements. */
export function readApiBaseUrl(source: ImportMetaEnv = import.meta.env): string {
  return (
    optional(source.VITE_API_BASE_URL) ??
    API_BASE_URL_BY_ENV[normalizeDeployEnv(source.VITE_GIVMO_ENV)]
  );
}

/** The environment and the API's base URL, without the boot-time requirements (the assistant's mount
 *  reads them per render, as the company console's does). */
export function readApiEndpoint(source: ImportMetaEnv = import.meta.env): {
  deployEnv: DeployEnv;
  apiBaseUrl: string;
} {
  return {
    deployEnv: normalizeDeployEnv(source.VITE_GIVMO_ENV),
    apiBaseUrl: readApiBaseUrl(source),
  };
}

/** Read the validated env subset from Vite's `import.meta.env`. */
export function readCharityEnv(): CharityEnv {
  return parseCharityEnv(import.meta.env);
}

/** Whether this is a production-grade environment (drives HSTS / debug-log gating). */
export function isProduction(env: CharityEnv): boolean {
  return env.deployEnv === 'prod';
}

/**
 * Whether the §321 fundraiser-attribution rows section (FCA-WEB1) is enabled. A tiny typed accessor
 * over the centralized boolean coercion. It intentionally reads only its own build-time flag so
 * module initialization cannot bypass the guarded app boot with an unrelated Firebase config error.
 */
export function isFundraiserRowsUiEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_FUNDRAISER_ROWS_UI_ENABLED);
}

// The served Charity Terms page (`/terms`), its footer link, its account-menu row and its
// getting-started step are always compiled in: the build knob that once gated them
// (`VITE_CHARITY_TERMS_UI_ENABLED`) was retired by CPA-WEB67 after its gate — a President-signed
// effective Charity Terms version — was met on 2026-07-20 and the knob had read `true` in every
// environment. The GitHub variable is deleted after this code has served a train (expand-contract).

// The claim wizard always runs the bundled ceremony — the Charity Terms read in flow and accepted with
// the claim (CPA-WEB12, D-CP-37): the build knob that once chose between it and the attestation-only
// screen (`VITE_CHARITY_CLAIM_BUNDLED_TERMS_ENABLED`) was retired by CPA-WEB87 after it had read `true`
// in every environment since 2026-09-12, and its backend pair (`CHARITY_CLAIM_BUNDLED_TERMS_ENABLED`)
// was removed by FLG-BE5b, which makes the bundled claim the backend's only claim path. The GitHub
// variable is deleted after this code serves on prod (expand-contract, as for CPA-WEB67 above).

/** The module route family, square, sidebar and door reveal together. */
export function isFundraisersModuleEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_FUNDRAISERS_MODULE_ENABLED);
}

/**
 * Whether the Fundraisers page carries its "Started by supporters" tab (FCM-WEB3). The charity flavor
 * reads it per render, so the tab, its count and its `?tab=` address reveal together; nothing else
 * moves. Its paired backend gate is the opt-out retirement flag plus the module flags — flip this only
 * after they are on in the same environment.
 */
export function isSupporterFundraisersEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_SUPPORTER_FUNDRAISERS_ENABLED);
}

/**
 * Whether the organization QR design editor is revealed (FCM-WEB4): the share panel's "Customize"
 * and the `/fundraisers/$fundraiserId/qr` page — and, with {@link isProfilePageEnabled}, the Profile
 * page's QR card and its `/profile/qr` page (PE-WEB11). Routes and placements read this ONE accessor.
 * The backend's `ORG_QR_DESIGN_ENABLED` stays authoritative: dark, the editor shows its calm notice.
 */
export function isOrgQrDesignEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_ORG_QR_DESIGN_ENABLED);
}

/**
 * Whether the Profile page carries its Branding and Collateral tabs (EMC-WEB11; D-EMC-20 (c), (d)).
 * Read by the Profile page alone, and only while {@link isProfilePageEnabled} has revealed it. Flip it
 * with or after the backend's `EMAIL_CAMPAIGNS_ENABLED` in the same environment.
 */
export function isOrgBrandingEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_ORG_BRANDING_ENABLED);
}

/**
 * Whether New fundraiser opens the Fundraiser Creation Wizard (EMC-WEB7) instead of the one-page create
 * form. The mount reads it per render into the module's config, so the create page alone changes; the
 * editor, a reopened draft and every other page are the same either way. Flip it only where the
 * backend's `CHARITY_P2P_ENABLED` and `CAMPAIGN_ENDS_ENABLED` are on in the same environment.
 */
export function isFundraiserWizardEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_FUNDRAISER_WIZARD_ENABLED);
}

/**
 * Whether the creation wizard asks a campaign "Will people raise in groups?". The mount reads it per
 * render into the module's config. Groups aren't built: off, a campaign's path skips the question, and
 * on, the answer decides whether the campaign has teams. It needs no backend flag.
 */
export function isCampaignGroupsEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_CAMPAIGN_GROUPS_ENABLED);
}

/** Books mounts only in a build explicitly configured to serve it. */
export function isBooksModuleEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_BOOKS_MODULE_ENABLED);
}

/**
 * Whether the tabbed Profile PAGE (Profile Enrichment PE-WEB1, D-PE-12) is revealed in this env
 * build: `/profile` renders the page (Details · Page content · View as public) in place of the
 * CPA-WEB6 editor, and the Organization row requires `profile:read`. The route and the row consume
 * this ONE accessor so both reveal together; UNSET everywhere today. It decides nothing about
 * serving: the backend's `PROFILE_ENRICHMENT_ENTITY_TYPES` allowlist is the serving gate both
 * directions — with the knob on and the class dark, the page's content tab shows its honest
 * unavailable state (inferred from the owner read's 404), never dead controls.
 */
export function isProfilePageEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_PROFILE_PAGE_ENABLED);
}

/** Whether the charity-owned donor analytics route/nav is revealed under its independent gate. */
export function isCharityDonorAnalyticsEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_CHARITY_DONOR_ANALYTICS_ENABLED);
}

/**
 * Whether the compliance filing register (CCM-WEB1) is revealed in this env build. The app shell
 * (nav items) and the router (route registration) both consume this accessor, so while the knob is
 * off there is no sidebar entry AND no direct-URL entry point — the other module reveal posture.
 *
 * The backend `CHARITY_COMPLIANCE_MODULE_ENABLED` flag (layered under `CHARITY_PORTAL_ENABLED`)
 * stays authoritative at runtime regardless of this knob: every `/charity-admin/compliance`
 * operation answers 404 while it is off, and the module's surfaces render their not-available state
 * rather than an error when that happens.
 */
export function isComplianceUiEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_COMPLIANCE_UI_ENABLED);
}

/**
 * Whether the back-office enrollment surface (CBO-IN3) is revealed in this env build. The router
 * (route registration) and the dashboard (its getting-started step) both consume this accessor, so
 * while the knob is off there is no direct-URL entry point AND no step pointing at one — the
 * compliance/CRM posture, minus the sidebar half, because enrollment is a once-ever act and earns no
 * permanent nav row (the D-CP-36 reasoning that retired the Claim group).
 *
 * The backend `CHARITY_BACKOFFICE_ENABLED` flag (layered under `CHARITY_PORTAL_ENABLED`) stays
 * authoritative at runtime regardless of this knob: every `/charity-admin/enrollment` operation
 * answers 404 while it is off, and the surfaces render their not-available state rather than an
 * error when that happens.
 */
export function isEnrollmentUiEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_ENROLLMENT_UI_ENABLED);
}

/**
 * Environment-matched origin for links into the public donate app. This mirrors the byte-pinned
 * cross-app host mapping used elsewhere in the repo: production has the bare host, staging has the
 * staging host, and local/development both use the development host.
 */
export function getDonateWebBaseUrl(
  env: DeployEnv = normalizeDeployEnv(import.meta.env.VITE_GIVMO_ENV),
): string {
  return donateWebBaseUrl(env);
}

// ── The class-mismatch forward (CPA-WEB40 · contract §Forward) ─────────────────────────────────

/**
 * The company console's origin — where a caller who signed in here but administers a COMPANY is
 * forwarded. UNDEFAULTED env-config, exactly like every module URL (and unlike the donate origin
 * above, which is a link into a public app rather than a redirect off this one): a build that was
 * not given it forwards nobody, and the person keeps the claim wizard. A guessed host here would
 * bounce someone off this console into nothing. Unset in every environment today.
 */
export function getCompanyConsoleUrl(source: ImportMetaEnv = import.meta.env): string | null {
  return optional(source.VITE_COMPANY_CONSOLE_URL) ?? null;
}

/**
 * The wire's word for this console's own kind of organization.
 *
 * ⚠ IT IS THE BACKEND'S VOCABULARY, NOT THIS APP'S, and it is deliberately declared here rather than
 * inside `@givmo/org-console` — that package is built once for every kind of organization and never
 * learns which one it is rendering. A host naming its own class is fine; the shared machinery naming
 * anybody's is not.
 */
export const OWN_ENTITY_CLASS = 'charity';

/** The wire's word for the kind of organization the company console serves. */
export const FOR_PROFIT_ENTITY_CLASS = 'for_profit';

/**
 * Where a caller this console cannot serve is forwarded, keyed by the wire's `entity_class`.
 *
 * ⚠ THIS CONSOLE'S OWN CLASS IS DELIBERATELY ABSENT. The map is "organizations I cannot render and
 * where they belong", so omission is how the rule stays honest without the shared hook ever learning
 * what class this host is. A class present with `null` is one this build has no destination for:
 * still never forwarded.
 */
export function mismatchForwardDestinations(
  source: ImportMetaEnv = import.meta.env,
): Record<string, string | null> {
  return { [FOR_PROFIT_ENTITY_CLASS]: getCompanyConsoleUrl(source) };
}

/** The existing Board reveal knob, shared with its legacy compatibility host. */
export function isBoardUiEnabled(): boolean {
  return booleanFlag(import.meta.env.VITE_BOARD_UI_ENABLED);
}
